import type { JSX } from 'react';

/** One saved value, flattened for display. */
export interface CapturedVariableEntry {
  name: string;
  value: string;
  source?: string;
  /** "step 2" or "precondition 1": where the value was saved, when the service says. */
  step?: string;
}

/** Display text for a saved value: strings as they are, anything else as JSON, nothing as "". */
function displayValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined) return '';
  return JSON.stringify(value) ?? String(value);
}

/** "step N" / "precondition N" from the 0-based `phase` + `index` the worker reports. */
function stepLabel(phase: unknown, index: unknown): string | undefined {
  if (typeof index !== 'number' || !Number.isInteger(index) || index < 0)
    return undefined;
  if (phase === 'step') return `step ${index + 1}`;
  if (phase === 'precondition') return `precondition ${index + 1}`;
  return undefined;
}

/**
 * The values a case saved from API responses (MEDA-865), in the order the service sent them. The
 * field is cast, not validated, so anything that is not a map of `{ value, source }` objects reads
 * as nothing saved.
 */
export function capturedVariableEntries(
  variables: unknown,
): CapturedVariableEntry[] {
  if (!variables || typeof variables !== 'object' || Array.isArray(variables))
    return [];
  return Object.entries(variables).flatMap(([name, entry]) => {
    if (!entry || typeof entry !== 'object' || !('value' in entry)) return [];
    const { value, source, phase, index } = entry as {
      value: unknown;
      source?: unknown;
      phase?: unknown;
      index?: unknown;
    };
    const step = stepLabel(phase, index);
    return [
      {
        name,
        value: displayValue(value),
        ...(typeof source === 'string' ? { source } : {}),
        ...(step ? { step } : {}),
      },
    ];
  });
}

/** Lists the values a case saved from API responses. Renders nothing when it saved none. */
export function CapturedVariables({
  entries,
}: {
  entries: CapturedVariableEntry[];
}): JSX.Element | null {
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs text-muted-foreground/80">
        Saved from API responses:
      </p>
      {entries.map((entry) => (
        <div
          key={entry.name}
          className="flex min-w-0 items-baseline gap-2 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs"
        >
          <span className="min-w-0 break-all font-mono">
            {`${entry.name} = ${entry.value}`}
          </span>
          {entry.source || entry.step ? (
            <span className="ml-auto shrink-0 text-muted-foreground">
              {[entry.source ? `from ${entry.source}` : null, entry.step]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}
